import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import * as moment from "moment";
import { Subscription } from "rxjs";
import { CATEGORY_LABELS, marketCategoryLabel } from "src/_constants/market_categories";
import {
  HoldReportDay,
  HoldReportEventInfo,
  HoldReportEventRow,
  HoldReportListEvent,
  HoldReportMarketRow,
  HoldReportMarketTypeRow,
  HoldReportPhase,
  HoldReportRangeTotals,
  HoldReportRangeTypeRow,
  HoldReportReq,
  HoldReportResp,
  HoldReportSavedTotals,
  HoldReportStats,
  HoldReportWindowRow,
} from "src/_models";
import { AppService } from "src/_services/app.service";

type View = "date" | "event" | "market";

// One entry per table column: adding a column to a table is adding an entry to its list.
interface Column<T> {
  label: string;
  cell: (row: T) => string;
  num?: boolean;
  title?: string;
  timeline?: boolean;
  // A number from the nightly roll-up: a row it has not finalised shows one tag across these
  // columns instead, so they come last.
  rollup?: boolean;
  tone?: (row: T) => string;
  // Makes the header clickable; null sorts last either way.
  sortKey?: (row: T) => number;
}

interface Row<T> {
  data: T;
  cells: string[];
  tones?: string[];
}

interface PhaseSpan {
  leftPct: number;
  widthPct: number;
  title: string;
  suspended: boolean;
}

interface WindowRow extends Row<HoldReportWindowRow> {
  phases: PhaseSpan[];
  edge: boolean;
}

interface MarketTypeOption {
  srGroup: string;
  label: string;
}

interface MarketTypeSummary extends HoldReportMarketTypeRow {
  gpBets: number;
  marketsWithBets: number;
}

interface DaySummary extends HoldReportDay {
  // Saved GGR of the finalised days up to and including this one.
  runningSavedGgr: number;
}

interface Kpi {
  label: string;
  value: string;
  title?: string;
  tone?: string;
  // Shown in green before the value.
  highlight?: string;
}

interface CacheEntry {
  res: HoldReportResp;
  at: number;
}

interface LinePart {
  text: string;
  strong?: boolean;
  tone?: string;
}

interface InfoLine {
  label: string;
  parts: LinePart[];
}

interface MarketHeader {
  title: string;
  subtitle: string;
  lines: InfoLine[];
  footnote: string;
}

const IST_OFFSET_MINUTES = 330;
const MAX_RANGE_DAYS = 31;
const DARK_MODE_KEY = "hr-dark-mode";
const LIVE_CACHE_MS = 2 * 60 * 1000;
const FINISHED_AFTER_MS = 24 * 60 * 60 * 1000;
const CACHE_SIZE = 5;
const SAVED_BETS_TITLE = "GP bets placed while SR's price was on our board";
// Genie deletes hold windows after 60 days, so the backend rebuilds back to today-59 (IST).
const REBUILD_MAX_AGE_DAYS = 59;
// The Leak Ledger's trend line colour.
const COLOR_TREND = "#6C5CE7";

const START_STATUS_TEXT: { [status: string]: string } = {
  BALL_RUNNING: "Tommy said BALL_RUNNING",
  SUSPENDED: "Tommy said SUSPENDED",
  DROPPED: "Tommy dropped it",
  ZERO_PRICE: "Tommy sent a zero price",
};

const END_REASON_TEXT: { [reason: string]: string } = {
  PROVIDER_PRICED: "provider is pricing this market again",
  LAPSED: "LAPSED — 10-min cap expired",
  MARKET_CLOSED: "market closed or settled",
  NO_PREMIUM_MARKET: "no premium market",
  READ_ERROR: "Genie could not read the market",
  CONVERSION_EMPTY: "price conversion returned nothing",
};

const moneyFormat = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const countFormat = new Intl.NumberFormat("en-IN");

function formatMoney(value: number): string {
  const amount = value || 0;
  return (amount < 0 ? "-₹" : "₹") + moneyFormat.format(Math.abs(amount));
}

function formatCount(value: number): string {
  return countFormat.format(value || 0);
}

// One decimal, or "—" when there is nothing to divide by.
function percent(part: number, whole: number): string {
  return whole ? `${((part / whole) * 100).toFixed(1)}%` : "—";
}

// Saved GGR %: red below 0%, yellow under 5%, green from 5% up.
function ggrTone(ggr: number, turnover: number): string {
  if (!turnover) {
    return "";
  }
  const pct = (ggr / turnover) * 100;
  return pct < 0 ? "hr-tone-bad" : pct < 5 ? "hr-tone-warn" : "hr-tone-good";
}

function formatHold(ms: number): string {
  const totalSec = Math.max(Math.round((ms || 0) / 1000), 0);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h) {
    return `${h}h ${m}m ${s}s`;
  }
  return m ? `${m}m ${s}s` : `${s}s`;
}

// Analysis-style duration to a tenth of a second: "45.2s", "6m22.2s", "55m10.6s", "1h02m05.0s".
function formatSeconds(ms: number): string {
  const tenths = Math.round(Math.max(ms || 0, 0) / 100);
  const h = Math.floor(tenths / 36000);
  const m = Math.floor((tenths % 36000) / 600);
  const s = ((tenths % 600) / 10).toFixed(1);
  const paddedS = s.length < 4 ? `0${s}` : s;
  if (h) {
    return `${h}h${m < 10 ? "0" : ""}${m}m${paddedS}s`;
  }
  return m ? `${m}m${paddedS}s` : `${s}s`;
}

function plainSeconds(ms: number): string {
  return `${Math.round((ms || 0) / 100) / 10}s`;
}

function formatIst(ms: number, pattern: string): string {
  return ms ? moment(ms).utcOffset(IST_OFFSET_MINUTES).format(pattern) : "—";
}

function todayIst(): string {
  return moment().utcOffset(IST_OFFSET_MINUTES).format("YYYY-MM-DD");
}

// A YYYY-MM-DD day from the backend, which already counts days in IST.
function formatDay(date: string): string {
  return moment(date, "YYYY-MM-DD").format("D MMM YYYY");
}

// "3 Oct 2026", or "29 Sep – 3 Oct 2026" for a range.
function formatDays(from: string, to: string): string {
  const start = moment(from, "YYYY-MM-DD");
  if (from === to) {
    return start.format("D MMM YYYY");
  }
  const end = moment(to, "YYYY-MM-DD");
  return `${start.format(start.year() === end.year() ? "D MMM" : "D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
}

function shiftDay(date: string, days: number): string {
  return moment(date, "YYYY-MM-DD").add(days, "days").format("YYYY-MM-DD");
}

// The days the backend can rebuild, from today-59 to yesterday (IST).
function rebuildWindow(): { oldest: string; last: string } {
  const today = todayIst();
  return { oldest: shiftDay(today, -REBUILD_MAX_AGE_DAYS), last: shiftDay(today, -1) };
}

function finalisedNote(t: HoldReportRangeTotals): string {
  const note = t.finalisedThrough ? `Finalised through ${formatDay(t.finalisedThrough)}` : "Nothing finalised in this range yet";
  const n = t.notFinalised;
  return n > 0 ? `${note} · excludes ${formatCount(n)} ${n === 1 ? "event" : "events"} not finalised yet` : note;
}

function rangeError(from: string, to: string): string {
  const start = moment.utc(from, "YYYY-MM-DD", true);
  const end = moment.utc(to, "YYYY-MM-DD", true);
  if (!start.isValid() || !end.isValid()) {
    return "Pick both a From and a To date.";
  }
  if (start.isAfter(end)) {
    return "The From date must be on or before the To date.";
  }
  if (end.diff(start, "days") > MAX_RANGE_DAYS) {
    return `Pick dates at most ${MAX_RANGE_DAYS} days apart.`;
  }
  return null;
}

// The Leak Ledger's presets, ending today (IST) instead of two days ago, plus Today.
function rangePresets(): { [label: string]: moment.Moment[] } {
  const today = moment(todayIst(), "YYYY-MM-DD");
  const lastDays = (n: number) => [today.clone().subtract(n - 1, "days"), today.clone().endOf("day")];
  const lastMonth = today.clone().subtract(1, "month");
  return {
    Today: lastDays(1),
    "Last 7 Days": lastDays(7),
    "Last 14 Days": lastDays(14),
    "Last 30 Days": lastDays(30),
    "This Month": [today.clone().startOf("month"), today.clone().endOf("day")],
    "Last Month": [lastMonth.clone().startOf("month"), lastMonth.clone().endOf("month")],
  };
}

// Older links carry a single ?date=, read as a one-day range; an invalid range falls back to today.
function rangeFromParams(params: ParamMap): { from: string; to: string } {
  const from = params.get("from") || params.get("date");
  const to = params.get("to") || from;
  if (rangeError(from, to)) {
    const today = todayIst();
    return { from: today, to: today };
  }
  return { from, to };
}

function humanize(code: string): string {
  return code
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function marketTypeLabel(srGroup: string, marketType: string): string {
  if (srGroup && !CATEGORY_LABELS[srGroup] && marketType) {
    return `${humanize(marketType)} (${srGroup})`;
  }
  if (srGroup) {
    return marketCategoryLabel(srGroup);
  }
  return marketType ? humanize(marketType) : "—";
}

function whyHeld(w: HoldReportWindowRow): string {
  const status = START_STATUS_TEXT[w.startStatus] || w.startStatus || "—";
  const phases = w.phases || [];
  if (phases.some((p) => p.state === "PRICED")) {
    return `${status}; SR live at ${w.srLineAtStart}`;
  }
  const suspended = phases.filter((p) => p.state === "SUSPENDED")[0];
  return suspended && suspended.reason ? `${status}; ${suspended.reason}` : status;
}

function releaseReason(w: HoldReportWindowRow): string {
  const text = END_REASON_TEXT[w.endReason] || w.endReason || "—";
  if (w.endReason !== "LAPSED") {
    return text;
  }
  return w.providerBackAt
    ? `${text}, off board ${formatSeconds(w.providerBackAt - w.endedAt)} until Tommy's first price`
    : `${text}; Tommy never priced it again`;
}

// Genie starts a new SUSPENDED phase when only the reason changes, so back-to-back SUSPENDED
// phases are one pause.
function pausesAfterFirstPriced(phases: HoldReportPhase[]): number {
  const list = phases || [];
  let pauses = 0;
  let priced = false;
  for (let i = 0; i < list.length; i++) {
    if (list[i].state === "PRICED") {
      priced = true;
    } else if (priced && list[i].state === "SUSPENDED" && list[i - 1].state !== "SUSPENDED") {
      pauses++;
    }
  }
  return pauses;
}

function savedMoneyColumns<T extends Pick<HoldReportStats, "savedTurnover" | "savedGgr">>(): Column<T>[] {
  return [
    { label: "Saved turnover", num: true, cell: (r) => formatMoney(r.savedTurnover), sortKey: (r) => r.savedTurnover },
    { label: "Saved GGR", num: true, cell: (r) => formatMoney(r.savedGgr), sortKey: (r) => r.savedGgr },
    {
      label: "Saved GGR %",
      num: true,
      cell: (r) => percent(r.savedGgr, r.savedTurnover),
      tone: (r) => ggrTone(r.savedGgr, r.savedTurnover),
      sortKey: (r) => (r.savedTurnover ? r.savedGgr / r.savedTurnover : null),
    },
  ];
}

// The date view's numbers for an event or a day.
function rollupColumns<T extends HoldReportSavedTotals>(): Column<T>[] {
  const columns: Column<T>[] = [
    { label: "GP bets", num: true, cell: (r) => formatCount(r.gpBets) },
    {
      label: "Saved by fix",
      num: true,
      title: SAVED_BETS_TITLE,
      cell: (r) => `${formatCount(r.savedBets)} of ${formatCount(r.gpBets)} (${percent(r.savedBets, r.gpBets)})`,
    },
    ...savedMoneyColumns<T>(),
  ];
  return columns.map((c) => ({ ...c, rollup: true }));
}

function sortRows<T>(rows: Row<T>[], key: (row: T) => number, desc: boolean): Row<T>[] {
  return rows.slice().sort((a, b) => {
    const x = key(a.data);
    const y = key(b.data);
    if (x === null || y === null) {
      return x === y ? 0 : x === null ? 1 : -1;
    }
    return desc ? y - x : x - y;
  });
}

function textPart(text: string): LinePart {
  return { text };
}

function numberPart(text: string, tone?: string): LinePart {
  return { text, strong: true, tone };
}

// GP bets per type come from its markets' gpTotal (every bet on the market, not only those inside
// holds); types without any are left out.
function summarizeMarketTypes(types: HoldReportMarketTypeRow[], markets: HoldReportMarketRow[]): MarketTypeSummary[] {
  return (types || [])
    .map((t) => {
      const ofType = markets.filter((m) => m.srGroup === t.srGroup);
      return {
        ...t,
        gpBets: ofType.reduce((sum, m) => sum + m.gpTotal.bets, 0),
        marketsWithBets: ofType.filter((m) => m.gpTotal.bets > 0).length,
      };
    })
    .filter((t) => t.gpBets > 0);
}

// A day that is not finalised leaves the running total as it was.
function withRunningSavedGgr(days: HoldReportDay[]): DaySummary[] {
  let running = 0;
  return (days || []).map((d) => {
    if (d.finalised) {
      // Kept to the paisa like every amount from the backend, so float error never shows as -₹0.00.
      running = Math.round((running + d.savedGgr) * 100) / 100;
    }
    return { ...d, runningSavedGgr: running };
  });
}

// The Leak Ledger's daily trend chart, for the running saved GGR. A click anywhere on the plot
// passes the index of the day under the cursor, or -1 when the cursor is off the plot. Untyped like
// the Leak Ledger's: the ng-apexcharts version in package-lock.json types series data more narrowly.
function savedGgrChart(onClick: (dayIndex: number) => void): any {
  return {
    series: [],
    chart: {
      type: "area",
      height: 280,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "SF-Pro-Display-Regular",
      events: { click: (e: any, chart: any, options: any) => onClick(options.dataPointIndex) },
    },
    colors: [COLOR_TREND],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2.5 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 90, 100] } },
    markers: { size: 0, hover: { size: 5 } },
    grid: { borderColor: "#eef0f4", strokeDashArray: 4 },
    xaxis: { type: "datetime", labels: { format: "dd MMM" } },
    yaxis: { labels: { formatter: (v: number) => formatMoney(v) } },
    tooltip: { theme: "light", x: { format: "dd MMM yyyy" } },
    legend: { show: false },
  };
}

// A match 24h past its start is finished, so its response is reused until the page reloads; a live
// match's only for 2 minutes. The Refresh button always reloads.
function isFresh(entry: CacheEntry): boolean {
  const ev = entry.res.event;
  const finished = ev && ev.eventStartTime > 0 && Date.now() - ev.eventStartTime > FINISHED_AFTER_MS;
  return finished || Date.now() - entry.at < LIVE_CACHE_MS;
}

function marketCacheKey(srEventId: string, marketId: number, premiumMarketName: string): string {
  return `${srEventId}|${marketId}|${premiumMarketName}`;
}

function toRows<T>(items: T[], columns: Column<T>[]): Row<T>[] {
  return (items || []).map((data) => ({
    data,
    cells: columns.map((c) => c.cell(data)),
    tones: columns.map((c) => (c.tone ? c.tone(data) : "")),
  }));
}

@Component({
  selector: "app-hold-report",
  templateUrl: "./hold-report.component.html",
  styleUrls: ["./hold-report.component.scss"],
})
export class HoldReportComponent implements OnInit, OnDestroy {
  readonly eventColumns: Column<HoldReportListEvent>[] = [
    { label: "Start (IST)", cell: (e) => formatIst(e.eventStartTime, "D MMM, HH:mm") },
    { label: "Event", cell: (e) => e.eventName || e.srEventId },
    { label: "Competition", cell: (e) => e.competitionName || "—" },
    ...rollupColumns<HoldReportListEvent>(),
  ];

  readonly dayColumns: Column<DaySummary>[] = [
    { label: "Day", cell: (d) => formatDay(d.date) },
    { label: "Events", num: true, cell: (d) => formatCount(d.events) },
    ...rollupColumns<DaySummary>(),
    { label: "Running saved GGR", num: true, rollup: true, cell: (d) => formatMoney(d.runningSavedGgr) },
  ];

  readonly dayRollupSpan = this.dayColumns.filter((c) => c.rollup).length;

  readonly rangeTypeColumns: Column<HoldReportRangeTypeRow>[] = [
    { label: "Market type", cell: (t) => marketTypeLabel(t.srGroup, t.marketType) },
    {
      label: "Markets",
      num: true,
      title: "Markets of this type with GP bets, added up over the events",
      cell: (t) => formatCount(t.markets),
    },
    { label: "GP bets", num: true, cell: (t) => formatCount(t.gpBets) },
    {
      label: "Saved by fix",
      num: true,
      title: SAVED_BETS_TITLE,
      cell: (t) => `${formatCount(t.savedBets)} (${percent(t.savedBets, t.gpBets)})`,
    },
    ...savedMoneyColumns<HoldReportRangeTypeRow>(),
  ];

  readonly marketTypeColumns: Column<MarketTypeSummary>[] = [
    { label: "Market type", cell: (t) => marketTypeLabel(t.srGroup, t.marketType) },
    { label: "Markets", num: true, title: "Markets of this type with GP bets", cell: (t) => formatCount(t.marketsWithBets) },
    { label: "GP bets", num: true, cell: (t) => formatCount(t.gpBets) },
    { label: "Saved by fix", num: true, title: SAVED_BETS_TITLE, cell: (t) => `${formatCount(t.savedBets)} (${percent(t.savedBets, t.gpBets)})` },
    ...savedMoneyColumns<MarketTypeSummary>(),
  ];

  readonly marketColumns: Column<HoldReportMarketRow>[] = [
    { label: "Market", cell: (m) => m.marketName || "—" },
    { label: "Premium market", cell: (m) => m.premiumMarketName || "—" },
    { label: "Market type", cell: (m) => marketTypeLabel(m.srGroup, m.marketType) },
    { label: "GP bets", num: true, cell: (m) => formatCount(m.gpTotal.bets), sortKey: (m) => m.gpTotal.bets },
    {
      label: "Saved by fix",
      num: true,
      title: SAVED_BETS_TITLE,
      cell: (m) => `${formatCount(m.savedBets)} (${percent(m.savedBets, m.gpTotal.bets)})`,
      sortKey: (m) => m.savedBets,
    },
    ...savedMoneyColumns<HoldReportMarketRow>(),
  ];

  readonly marketSummaryColumns: Column<HoldReportMarketRow>[] = [
    { label: "Market", cell: (m) => m.marketName || m.premiumMarketName || "—" },
    { label: "marketId", num: true, cell: (m) => `${m.marketId}` },
    { label: "GP bets", num: true, cell: (m) => formatCount(m.gpTotal.bets) },
    { label: "Saved by fix", num: true, title: SAVED_BETS_TITLE, cell: (m) => `${formatCount(m.savedBets)} of ${formatCount(m.gpTotal.bets)}` },
    {
      label: "Timing of saved bets",
      cell: (m) =>
        m.firstSavedAt
          ? `${formatIst(m.firstSavedAt, "HH:mm:ss")} → ${formatIst(m.lastSavedAt, "HH:mm:ss")} (${m.savedWindowCount} holds)`
          : "—",
    },
  ];

  readonly windowColumns: Column<HoldReportWindowRow>[] = [
    { label: "Hold window (IST)", cell: (w) => `${formatIst(w.startedAt, "HH:mm:ss")} – ${formatIst(w.endedAt, "HH:mm:ss")}` },
    { label: "Dur", num: true, cell: (w) => formatSeconds(w.holdMs) },
    { label: "SR priced", num: true, timeline: true, cell: (w) => formatSeconds(w.pricedMs) },
    { label: "Bets", num: true, title: SAVED_BETS_TITLE, cell: (w) => formatCount(w.savedBets) },
    { label: "Turnover", num: true, cell: (w) => formatMoney(w.savedTurnover) },
    { label: "GGR", num: true, cell: (w) => formatMoney(w.savedGgr) },
    { label: "Why we held", cell: (w) => whyHeld(w) },
    { label: "Release reason", cell: (w) => releaseReason(w) },
  ];

  view: View = "date";
  readonly ranges = rangePresets();
  selected: { startDate: moment.Moment; endDate: moment.Moment } = null;
  eventIdInput = "";
  inputError: string = null;
  loading = false;
  loadingText = "";
  errorText: string = null;

  rangeKpis: Kpi[] = [];
  rangeNote = "";
  days: Row<DaySummary>[] = [];
  // The finalised days, one per point on the chart.
  chartDays: string[] = [];
  rangeTypes: Row<HoldReportRangeTypeRow>[] = [];
  events: Row<HoldReportListEvent>[] = [];
  visibleEvents: Row<HoldReportListEvent>[] = [];
  // eventColumns, less the roll-up ones when the response has no range totals.
  shownEventColumns = this.eventColumns;
  eventRollupSpan = 0;
  eventSearch = "";
  // The day (YYYY-MM-DD) picked on the chart or in the By day table; null lists every day.
  eventDay: string = null;
  // The rebuild endpoint is SAPAdmin-only.
  readonly isSapAdmin = (JSON.parse(sessionStorage.getItem("currentUser")) || {}).role === "SAPAdmin";
  rebuilding = false;
  private eventsRange: string = null;

  event: HoldReportEventRow = null;
  eventKpis: Kpi[] = [];
  marketTypes: Row<MarketTypeSummary>[] = [];
  marketTypeOptions: MarketTypeOption[] = [];
  marketTypeFilter = "";
  onlyMarketsWithBets = true;
  markets: Row<HoldReportMarketRow>[] = [];
  visibleMarkets: Row<HoldReportMarketRow>[] = [];
  // Index into marketColumns; null keeps the backend's order.
  marketSort: { index: number; desc: boolean } = null;

  marketEvent: HoldReportEventRow = null;
  market: HoldReportMarketRow = null;
  marketName = "";
  marketHeader: MarketHeader = null;
  marketSummaryCells: string[] = [];
  windows: WindowRow[] = [];
  visibleWindows: WindowRow[] = [];
  windowTotalCells: string[] = [];
  onlySavedHolds = true;
  darkMode = localStorage.getItem(DARK_MODE_KEY) === "true";
  savedChart: any = this.applyChartTheme(savedGgrChart((i) => this.pickChartDay(i)));

  @ViewChild("eventsCard") private eventsCard: ElementRef;
  private allMarketTypeOptions: MarketTypeOption[] = [];
  // The range the page is showing; `selected` is the picker's value, which the user can change.
  private activeFrom = todayIst();
  private activeTo = todayIst();
  private requestedEventId: string = null;
  private requestedMarketId = 0;
  private requestedPm = "";
  // The last few event and market responses, by srEventId and by marketCacheKey.
  private eventCache: { [key: string]: CacheEntry } = {};
  private marketCache: { [key: string]: CacheEntry } = {};
  // Bumped on every view change, so a response for a view the user has already left is dropped.
  // Calls are never cancelled instead: the app-wide loading overlay only clears when a call ends.
  private requestSeq = 0;
  private paramsSub: Subscription;

  constructor(private appService: AppService, private router: Router, private route: ActivatedRoute) {}

  ngOnInit() {
    this.paramsSub = this.route.queryParamMap.subscribe((params) => this.show(params));
  }

  ngOnDestroy() {
    this.requestSeq++;
    if (this.paramsSub) {
      this.paramsSub.unsubscribe();
    }
  }

  get isOneDay(): boolean {
    return this.activeFrom === this.activeTo;
  }

  get rangeLabel(): string {
    const days = formatDays(this.activeFrom, this.activeTo);
    return this.isOneDay ? `on ${days}` : days;
  }

  // The picked range, clamped to the days the backend can rebuild; null when it has none of them.
  get rebuildBounds(): { from: string; to: string } {
    const w = rebuildWindow();
    const from = this.activeFrom > w.oldest ? this.activeFrom : w.oldest;
    const to = this.activeTo < w.last ? this.activeTo : w.last;
    return from <= to ? { from, to } : null;
  }

  get rebuildBoundsLabel(): string {
    const bounds = this.rebuildBounds;
    return bounds ? formatDays(bounds.from, bounds.to) : "";
  }

  get eventTitle(): string {
    const ev = this.view === "market" ? this.marketEvent : this.event;
    return ev && ev.srEventId === this.requestedEventId ? ev.eventName || ev.srEventId : "";
  }

  get eventDayLabel(): string {
    return this.eventDay ? formatDay(this.eventDay) : "";
  }

  onRangeChange(range: { startDate: moment.Moment; endDate: moment.Moment }) {
    if (!range || !range.startDate || !range.endDate) {
      return;
    }
    const from = range.startDate.format("YYYY-MM-DD");
    const to = range.endDate.format("YYYY-MM-DD");
    this.inputError = rangeError(from, to);
    if (this.inputError) {
      return;
    }
    // The router ignores a navigation to the URL already open, so retry the shown range directly.
    if (this.view === "date" && from === this.activeFrom && to === this.activeTo) {
      this.errorText = null;
      this.eventsRange = null;
      this.loadRange(from, to);
      return;
    }
    this.navigate({ from, to });
  }

  openEventById() {
    let id = (this.eventIdInput || "").trim();
    if (/^\d+$/.test(id)) {
      id = `sr:match:${id}`;
    }
    if (!/^sr:match:\d+$/.test(id)) {
      this.inputError = "Enter an event id such as sr:match:71040584 or 71040584.";
      return;
    }
    this.inputError = null;
    this.event = null;
    // Already open: the router would ignore the navigation, so show the event directly.
    if (this.view === "event" && this.requestedEventId === id) {
      this.errorText = null;
      this.loadEvent(id);
      return;
    }
    this.navigate({ event: id });
  }

  showDate() {
    this.navigate({});
  }

  showEvent() {
    this.navigate({ event: this.requestedEventId });
  }

  // Opening an event from the list starts it with fresh filters; the breadcrumb and browser Back keep them.
  openEvent(ev: HoldReportEventInfo) {
    this.event = null;
    this.navigate({ event: ev.srEventId });
  }

  refresh() {
    this.rebuilding = false;
    if (this.view === "date") {
      this.errorText = null;
      this.eventsRange = null;
      this.loadRange(this.activeFrom, this.activeTo);
    } else if (this.view === "event") {
      delete this.eventCache[this.requestedEventId];
      this.event = null;
      this.loadEvent(this.requestedEventId);
    } else if (this.view === "market") {
      delete this.marketCache[marketCacheKey(this.requestedEventId, this.requestedMarketId, this.requestedPm)];
      this.loadMarket(this.requestedEventId, this.requestedMarketId, this.requestedPm);
    }
  }

  openMarket(m: HoldReportMarketRow) {
    this.navigate({ event: this.requestedEventId, market: m.marketId, pm: m.premiumMarketName });
  }

  // With "Only markets with bets" on, markets without GP bets are hidden and so are the types left
  // with no market in the filter.
  applyMarketFilters() {
    const markets = this.onlyMarketsWithBets ? this.markets.filter((r) => r.data.gpTotal.bets > 0) : this.markets;
    this.marketTypeOptions = this.allMarketTypeOptions.filter((t) => markets.some((r) => r.data.srGroup === t.srGroup));
    if (!this.marketTypeOptions.some((t) => t.srGroup === this.marketTypeFilter)) {
      this.marketTypeFilter = "";
    }
    const visible = this.marketTypeFilter ? markets.filter((r) => r.data.srGroup === this.marketTypeFilter) : markets;
    this.visibleMarkets = this.marketSort
      ? sortRows(visible, this.marketColumns[this.marketSort.index].sortKey, this.marketSort.desc)
      : visible;
  }

  // A first click sorts largest first; clicking the same header again flips the order.
  sortMarkets(index: number) {
    if (!this.marketColumns[index].sortKey) {
      return;
    }
    const sameColumn = this.marketSort && this.marketSort.index === index;
    this.marketSort = { index, desc: sameColumn ? !this.marketSort.desc : true };
    this.applyMarketFilters();
  }

  canRebuildDay(date: string): boolean {
    const w = rebuildWindow();
    return date >= w.oldest && date <= w.last;
  }

  isToday(date: string): boolean {
    return date === todayIst();
  }

  // The button sits in a By day row, which picks the day when clicked.
  rebuildDay(date: string, click: Event) {
    click.stopPropagation();
    this.startRebuild(date, date);
  }

  rebuildRange() {
    const bounds = this.rebuildBounds;
    if (bounds) {
      this.startRebuild(bounds.from, bounds.to);
    }
  }

  applyEventSearch(search: string) {
    this.eventSearch = search;
    this.applyEventFilters();
  }

  // Narrows the events list to the events starting that day (IST) and scrolls to it, as it sits below
  // the By day and By market type tables.
  pickDay(date: string) {
    this.eventDay = date;
    this.applyEventFilters();
    this.eventsCard.nativeElement.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  showAllDays() {
    this.eventDay = null;
    this.applyEventFilters();
  }

  toggleDarkMode() {
    this.darkMode = !this.darkMode;
    localStorage.setItem(DARK_MODE_KEY, String(this.darkMode));
    this.savedChart = this.applyChartTheme(this.savedChart);
  }

  applyWindowFilter() {
    this.visibleWindows = this.onlySavedHolds
      ? this.windows.filter((r) => r.edge || r.data.savedBets > 0)
      : this.windows;
  }

  // Every view keeps the shown range in the URL, so the breadcrumb leads back to it.
  private navigate(params: { [key: string]: string | number }) {
    this.router.navigate([], { relativeTo: this.route, queryParams: { from: this.activeFrom, to: this.activeTo, ...params } });
  }

  // The URL is the source of truth (from / to / event / market), so Back, refresh and shared links
  // reopen the same view. Going back up a level reuses what was already loaded.
  private show(params: ParamMap) {
    this.requestSeq++;
    this.loading = false;
    // The app's HTTP interceptor swallows request errors, so a failed rebuild never reaches its
    // error callback; leaving the view re-enables the buttons.
    this.rebuilding = false;
    this.errorText = null;
    this.inputError = null;
    const range = rangeFromParams(params);
    this.activeFrom = range.from;
    this.activeTo = range.to;
    this.selected = { startDate: moment(range.from, "YYYY-MM-DD"), endDate: moment(range.to, "YYYY-MM-DD").endOf("day") };
    this.requestedEventId = params.get("event");
    this.requestedMarketId = Number(params.get("market")) || 0;
    this.requestedPm = params.get("pm") || "";
    if (this.requestedEventId && this.requestedMarketId) {
      this.view = "market";
      this.loadMarket(this.requestedEventId, this.requestedMarketId, this.requestedPm);
    } else if (this.requestedEventId) {
      this.view = "event";
      this.loadEvent(this.requestedEventId);
    } else {
      this.view = "date";
      this.loadRange(this.activeFrom, this.activeTo);
    }
  }

  private loadRange(fromDate: string, toDate: string) {
    const range = `${fromDate}/${toDate}`;
    if (this.eventsRange === range) {
      return;
    }
    this.request({ fromDate, toDate }, "Loading events…", (res) => {
      this.eventsRange = range;
      this.showRangeResponse(res);
    });
  }

  // Without range totals there are no roll-up numbers, so the events list keeps only Start, Event
  // and Competition.
  private showRangeResponse(res: HoldReportResp) {
    const totals = res.rangeTotals;
    this.rangeKpis = totals ? this.rangeKpiCards(totals) : [];
    this.rangeNote = totals ? finalisedNote(totals) : "";
    const days = withRunningSavedGgr(res.days);
    this.days = toRows(days, this.dayColumns);
    const finalised = days.filter((d) => d.finalised);
    this.chartDays = finalised.map((d) => d.date);
    this.savedChart = {
      ...this.savedChart,
      series: [
        {
          name: "Running saved GGR",
          data: finalised.map((d) => [moment.utc(d.date, "YYYY-MM-DD").valueOf(), d.runningSavedGgr]),
        },
      ],
    };
    this.rangeTypes = toRows((res.rangeByMarketType || []).filter((t) => t.gpBets > 0), this.rangeTypeColumns);
    this.shownEventColumns = totals ? this.eventColumns : this.eventColumns.filter((c) => !c.rollup);
    this.eventRollupSpan = this.shownEventColumns.filter((c) => c.rollup).length;
    this.events = toRows(res.events, this.shownEventColumns);
    this.eventDay = null;
    this.applyEventFilters();
  }

  // The search box and the picked day narrow the events list together.
  private applyEventFilters() {
    const query = (this.eventSearch || "").trim().toLowerCase();
    this.visibleEvents = this.events.filter(
      (r) =>
        (!this.eventDay || formatIst(r.data.eventStartTime, "YYYY-MM-DD") === this.eventDay) &&
        (!query || [r.data.eventName, r.data.competitionName, r.data.srEventId].some((v) => (v || "").toLowerCase().indexOf(query) >= 0))
    );
  }

  private startRebuild(fromDate: string, toDate: string) {
    this.rebuilding = true;
    this.appService.triggerHoldReportBackfill(fromDate, toDate).subscribe(
      (res) => {
        this.rebuilding = false;
        if (res && res.status === "RS_OK") {
          this.appService.openSuccessBar(
            `Rebuilding ${formatDays(fromDate, toDate)} in the background. Refresh in a minute or two.`
          );
        } else {
          this.appService.openErrorBar((res && res.errorDescription) || "Could not start the rebuild.");
        }
      },
      () => {
        this.rebuilding = false;
        this.appService.openErrorBar("Could not start the rebuild.");
      }
    );
  }

  private pickChartDay(index: number) {
    const day = this.chartDays[index];
    if (day) {
      this.pickDay(day);
    }
  }

  private loadEvent(srEventId: string) {
    // A Genie failure is not reused, so opening the event again retries Genie.
    if (this.event && this.event.srEventId === srEventId && this.event.genieStatus !== "GENIE_ERROR") {
      return;
    }
    const cached = this.cachedResponse(this.eventCache, srEventId);
    if (cached) {
      this.showEventResponse(cached);
      return;
    }
    this.request({ srEventId }, "Loading hold windows from Genie and matching bets… this can take a few seconds.", (res) => {
      this.cacheResponse(this.eventCache, srEventId, res);
      this.showEventResponse(res);
    });
  }

  private showEventResponse(res: HoldReportResp) {
    this.event = res.event;
    const markets = res.event ? res.event.markets : [];
    this.eventKpis = this.kpis(res.event);
    this.marketTypes = toRows(summarizeMarketTypes(res.byMarketType, markets), this.marketTypeColumns);
    this.allMarketTypeOptions = (res.byMarketType || []).filter((t) => t.srGroup).map((t) => ({
      srGroup: t.srGroup,
      label: marketTypeLabel(t.srGroup, t.marketType),
    }));
    this.marketTypeFilter = "";
    this.onlyMarketsWithBets = true;
    this.marketSort = null;
    this.markets = toRows(markets, this.marketColumns);
    this.applyMarketFilters();
  }

  private cachedResponse(cache: { [key: string]: CacheEntry }, key: string): HoldReportResp {
    const entry = cache[key];
    return entry && isFresh(entry) ? entry.res : null;
  }

  // Keeps the newest CACHE_SIZE responses; a Genie failure is never kept.
  private cacheResponse(cache: { [key: string]: CacheEntry }, key: string, res: HoldReportResp) {
    if (res.event && res.event.genieStatus === "GENIE_ERROR") {
      return;
    }
    delete cache[key];
    cache[key] = { res, at: Date.now() };
    const keys = Object.keys(cache);
    if (keys.length > CACHE_SIZE) {
      delete cache[keys[0]];
    }
  }

  private loadMarket(srEventId: string, marketId: number, premiumMarketName: string) {
    const req: HoldReportReq = { srEventId, marketId };
    if (premiumMarketName) {
      req.premiumMarketName = premiumMarketName;
    }
    this.marketEvent = null;
    this.market = null;
    this.marketName = premiumMarketName;
    this.marketHeader = null;
    this.onlySavedHolds = true;
    this.marketSummaryCells = [];
    this.windows = [];
    this.visibleWindows = [];
    this.windowTotalCells = [];
    const key = marketCacheKey(srEventId, marketId, premiumMarketName);
    const cached = this.cachedResponse(this.marketCache, key);
    if (cached) {
      this.showMarketResponse(cached, premiumMarketName);
      return;
    }
    this.request(req, "Loading this market's hold windows… this can take a few seconds.", (res) => {
      this.cacheResponse(this.marketCache, key, res);
      this.showMarketResponse(res, premiumMarketName);
    });
  }

  private showMarketResponse(res: HoldReportResp, premiumMarketName: string) {
    this.marketEvent = res.event;
    this.market = res.event && res.event.markets.length ? res.event.markets[0] : null;
    this.marketName = this.market ? this.market.marketName || this.market.premiumMarketName : premiumMarketName;
    const windows = res.windows || [];
    this.windows = windows.map((w, i) => ({
      data: w,
      cells: this.windowColumns.map((c) => c.cell(w)),
      phases: this.phaseSpans(w),
      edge: i === 0 || i === windows.length - 1,
    }));
    this.applyWindowFilter();
    if (this.market) {
      this.marketSummaryCells = this.marketSummaryColumns.map((c) => c.cell(this.market));
      this.marketHeader = this.buildMarketHeader(res.event, this.market, windows);
      this.windowTotalCells = [
        `TOTAL (${formatCount(windows.length)} holds)`,
        formatSeconds(this.market.holdMs),
        formatSeconds(this.market.pricedMs),
        formatCount(this.market.savedBets),
        formatMoney(this.market.savedTurnover),
        formatMoney(this.market.savedGgr),
        "",
        "",
      ];
    }
  }

  private request(req: HoldReportReq, loadingText: string, onOk: (res: HoldReportResp) => void) {
    const seq = ++this.requestSeq;
    this.loading = true;
    this.loadingText = loadingText;
    this.appService.getHoldReport(req).subscribe(
      (res) => {
        if (seq !== this.requestSeq) {
          return;
        }
        this.loading = false;
        if (res && res.status === "RS_OK") {
          onOk(res);
        } else {
          this.handleFailure(res);
        }
      },
      () => {
        if (seq !== this.requestSeq) {
          return;
        }
        this.loading = false;
        this.errorText = "Could not reach the server. Please try again.";
      }
    );
  }

  private handleFailure(res: HoldReportResp) {
    const description = res ? res.errorDescription : "";
    if (description === "Invalid Token!") {
      this.appService.openErrorBar(description);
      sessionStorage.clear();
      this.router.navigateByUrl("/login");
      return;
    }
    this.errorText = description || "Something went wrong.";
  }

  private rangeKpiCards(t: HoldReportRangeTotals): Kpi[] {
    return [
      { label: "Events", value: formatCount(t.events), title: "Finalised events in this range" },
      { label: "GP bets", value: formatCount(t.gpBets) },
      { label: "GP turnover", value: formatMoney(t.gpTurnover) },
      { label: "GP GGR", value: formatMoney(t.gpGgr) },
      {
        label: "Saved by fix",
        highlight: formatCount(t.savedBets),
        value: ` of ${formatCount(t.gpBets)} (${percent(t.savedBets, t.gpBets)})`,
        title: SAVED_BETS_TITLE,
      },
      { label: "Saved turnover", value: formatMoney(t.savedTurnover) },
      { label: "Saved GGR", value: formatMoney(t.savedGgr) },
      { label: "Saved GGR %", value: percent(t.savedGgr, t.savedTurnover), tone: ggrTone(t.savedGgr, t.savedTurnover) },
    ];
  }

  private kpis(ev: HoldReportEventRow): Kpi[] {
    if (!ev) {
      return [];
    }
    // Every market in the response, whatever the market filters are showing.
    const gp = ev.markets.reduce(
      (t, m) => ({ bets: t.bets + m.gpTotal.bets, turnover: t.turnover + m.gpTotal.turnover, ggr: t.ggr + m.gpTotal.ggr }),
      { bets: 0, turnover: 0, ggr: 0 }
    );
    return [
      { label: "GP bets", value: formatCount(gp.bets) },
      { label: "GP turnover", value: formatMoney(gp.turnover) },
      { label: "GP GGR", value: formatMoney(gp.ggr) },
      {
        label: "Saved by fix",
        highlight: formatCount(ev.savedBets),
        value: ` of ${formatCount(gp.bets)} (${percent(ev.savedBets, gp.bets)})`,
        title: SAVED_BETS_TITLE,
      },
      { label: "Saved turnover", value: formatMoney(ev.savedTurnover) },
      { label: "Saved GGR", value: formatMoney(ev.savedGgr) },
      { label: "Saved GGR %", value: percent(ev.savedGgr, ev.savedTurnover), tone: ggrTone(ev.savedGgr, ev.savedTurnover) },
    ];
  }

  private buildMarketHeader(ev: HoldReportEventRow, m: HoldReportMarketRow, windows: HoldReportWindowRow[]): MarketHeader {
    const last = windows.length ? windows[windows.length - 1] : null;

    const saved: LinePart[] = [
      numberPart(formatMoney(m.savedTurnover)),
      textPart(" turnover · GGR "),
      numberPart(formatMoney(m.savedGgr)),
      textPart(" ("),
      numberPart(percent(m.savedGgr, m.savedTurnover), ggrTone(m.savedGgr, m.savedTurnover)),
      textPart(")"),
    ];
    if (m.mappedAt) {
      saved.push(
        textPart(" · "),
        numberPart(formatCount(m.betsAfterMapping)),
        textPart(` bets after mapping at ${formatIst(m.mappedAt, "D MMM HH:mm:ss")}`)
      );
    }
    const lines: InfoLine[] = [{ label: "Saved by fix", parts: saved }];

    let footnote = "";
    if (last) {
      const dur = last.endedAt - last.startedAt;
      const hold: LinePart[] = [
        textPart(`${formatIst(last.startedAt, "HH:mm:ss.SSS")} → ${formatIst(last.endedAt, "HH:mm:ss.SSS")} · `),
        numberPart(plainSeconds(dur)),
      ];
      if (last.capMs) {
        hold.push(textPart(` of ${plainSeconds(last.capMs)} (${Math.round((dur / last.capMs) * 100)}%)`));
      }
      hold.push(textPart(` · ${releaseReason(last)}`));
      lines.push({ label: "Final hold", parts: hold });
      const started = (status: string) => windows.filter((w) => w.startStatus === status).length;
      footnote =
        `${started("BALL_RUNNING")} ball-running holds, ${started("SUSPENDED")} suspended holds, ${started("DROPPED")} dropped; ` +
        `${m.savedWindowCount} caught a bet. Final hold: repriced from SR ${last.repriceCount} times; ` +
        `SR paused ${pausesAfterFirstPriced(last.phases)} times.`;
    }

    const lastBets: LinePart[] = [textPart(`GP ${formatIst(m.lastGpBetAt, "HH:mm:ss")} · SR ${formatIst(m.lastSrBetAt, "HH:mm:ss")}`)];
    if (m.lastGpBetAt && m.lastSrBetAt > m.lastGpBetAt) {
      lastBets.push(textPart(" · SR kept taking bets for "), numberPart(formatHold(m.lastSrBetAt - m.lastGpBetAt)), textPart(" after us"));
    }
    // srAfterCap: the SR bets from the end of a lapsed hold until Tommy priced again or the bets end.
    if (last && last.srAfterCap && last.srAfterCap.bets > 0) {
      lastBets.push(
        textPart(" · "),
        numberPart(formatCount(last.srAfterCap.bets)),
        textPart(` SR bets after the hold ended at ${formatIst(last.endedAt, "HH:mm:ss")} · `),
        numberPart(formatMoney(last.srAfterCap.turnover)),
        textPart(" turnover · GGR "),
        numberPart(formatMoney(last.srAfterCap.ggr))
      );
    }
    lines.push({ label: "Last bet", parts: lastBets });

    return {
      title: `${m.marketName || m.premiumMarketName || "market " + m.marketId} — event ${ev.gpEventId || ev.srEventId} (${ev.eventName || ev.srEventId})`,
      subtitle: `${m.premiumMarketName || "—"} · ${marketTypeLabel(m.srGroup, m.marketType)}`,
      lines,
      footnote,
    };
  }

  // PRICED phases are drawn in colour and SUSPENDED ones muted; any other state is skipped.
  private phaseSpans(w: HoldReportWindowRow): PhaseSpan[] {
    const duration = w.endedAt - w.startedAt;
    if (!(duration > 0)) {
      return [];
    }
    return (w.phases || [])
      .filter((p) => (p.state === "PRICED" || p.state === "SUSPENDED") && p.to > p.from)
      .map((p) => {
        const from = Math.max(p.from, w.startedAt);
        const to = Math.min(p.to, w.endedAt);
        const suspended = p.state === "SUSPENDED";
        const label = suspended ? `Suspended${p.reason ? ` (${p.reason})` : ""}` : "SR priced";
        return {
          leftPct: ((from - w.startedAt) / duration) * 100,
          widthPct: (Math.max(to - from, 0) / duration) * 100,
          title: `${label} ${formatIst(from, "HH:mm:ss")} → ${formatIst(to, "HH:mm:ss")} IST`,
          suspended,
        };
      });
  }

  private themeTokens() {
    return this.darkMode
      ? { fg: "#9AA6B8", grid: "#2A2F3D", tooltipTheme: "dark" }
      : { fg: "#697483", grid: "#eef0f4", tooltipTheme: "light" };
  }

  private applyChartTheme(cfg: any): any {
    const t = this.themeTokens();
    return {
      ...cfg,
      chart: { ...cfg.chart, foreColor: t.fg, background: "transparent" },
      grid: { ...cfg.grid, borderColor: t.grid },
      tooltip: { ...cfg.tooltip, theme: t.tooltipTheme },
    };
  }
}
